import './AdAccount.scss';
import React from 'react';
import { uniqBy } from 'lodash';
import { connect } from 'react-redux';
import {
  compose,
  lifecycle,
  withState,
  withHandlers,
  mapProps,
} from 'recompose';
import { Dispatch } from 'redux';
import adAccountIcon from 'assets/ad-account.svg';
import SettingSection from '../../../../components/SettingSection/SettingSection';
import SelectorWithArrow from '../../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { StoreState } from '@mm/state/store-interfaces';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import adAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import { LifeCycleThis } from '@mm/utils';
import AdAccountModel, {
  emptyAccountPlaceholder,
} from '@mm/state/model/AdAccountModel';
import { addNamesToAccounts } from '../../../../../../../common/helpers/utils';
import { isFacebookConnectedSelector } from '@mm/state/selectors/userSelector';

type OuterProps = {};

type PropsFromWithState = {
  fbPageAccounts: AdAccountModel[];
  setFbPageAccounts: (fbPageAccounts: AdAccountModel[]) => void;
  remoteAccountsRequested: boolean;
  setRemoteAccountsRequested: (value: boolean) => void;
};
type MapStateToPropsType = ReturnType<typeof mapStateToProps>;
type MapDispatchToPropsType = ReturnType<typeof mapDispatchToProps>;

type PropsFromMapProps = {
  accountsWithNames: AdAccountModel[];
};
type PropsBeforeMapProps = PropsFromWithState &
  MapStateToPropsType &
  MapDispatchToPropsType;

type PropsFromHandlers = {
  handleChange: ReturnType<typeof handleChange>;
  onScrollEnd: ReturnType<typeof onScrollEnd>;
};

type InnerProps = PropsBeforeMapProps & PropsFromMapProps & PropsFromHandlers;

type Props = OuterProps & InnerProps;

const DESCRIPTION =
  'Please choose the ad account containing data on the Facebook page.';

const AdAccount = ({
  accountsWithNames,
  handleChange,
  getRemoteAccounts,
  onScrollEnd,
}: Props) => {
  const selectedAccount = (accountsWithNames || []).find(ac => ac.selected);
  return (
    <SettingSection title="Ad Account">
      <div className="add-account__description">{DESCRIPTION}</div>
      <div className="add-account__dropdown">
        <div className="add-account__oval">
          <img src={adAccountIcon} className="add-account__icon" />
        </div>
        <div className="add-account__select-with-arrow">
          <SelectorWithArrow
            onChange={handleChange}
            selectedItem={selectedAccount || accountsWithNames[0]}
            items={accountsWithNames}
            onScrollEndReached={onScrollEnd}
          />
        </div>
      </div>
    </SettingSection>
  );
};

const mapStateToProps = (state: StoreState) => ({
  remoteAccounts: state.adAccounts.entities,
  nextCursor: state.adAccounts.next,
  connectedPage: connectedPageSelector(state),
  isFacebookConnected: isFacebookConnectedSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getRemoteAccounts: (fbPageId: number | undefined, next: string = '') =>
    dispatch(adAccountCRUDActions.retrieveAll(fbPageId, false, next)),
  getFbPageAccounts: async (fbPageId: number) =>
    dispatch(adAccountCRUDActions.retrieveByFbPageId(fbPageId)),
  assignAdAccount: async (fbPageId: number, adAccount: AdAccountModel) =>
    dispatch(adAccountCRUDActions.assignAdAccount(fbPageId, adAccount, true)),
});

async function componentDidMount(this: LifeCycleThis<Props>) {
  const {
    remoteAccounts,
    getRemoteAccounts,
    connectedPage,
    getFbPageAccounts,
    setFbPageAccounts,
    remoteAccountsRequested,
    setRemoteAccountsRequested,
  } = this.props;
  if (remoteAccounts.length === 0 && !remoteAccountsRequested) {
    setRemoteAccountsRequested(true);
    await getRemoteAccounts(connectedPage && connectedPage.id);
  }
  if (connectedPage) {
    const fbPageAccounts = await getFbPageAccounts(connectedPage.id);
    setFbPageAccounts(fbPageAccounts);
  }
}

async function componentDidUpdate(
  this: LifeCycleThis<Props>,
  prevProps: Props,
) {
  const {
    remoteAccounts,
    getRemoteAccounts,
    connectedPage,
    getFbPageAccounts,
    setFbPageAccounts,
    remoteAccountsRequested,
    setRemoteAccountsRequested,
  } = this.props;

  const { connectedPage: prevConnectedPage } = prevProps;

  if (remoteAccounts.length === 0 && !remoteAccountsRequested) {
    setRemoteAccountsRequested(true);
    getRemoteAccounts(connectedPage && connectedPage.id);
  }

  if (connectedPage && connectedPage !== prevConnectedPage) {
    const fbPageAccounts = await getFbPageAccounts(connectedPage.id);
    setFbPageAccounts(fbPageAccounts);
  }
}

const propsMapper = (props: PropsBeforeMapProps) => {
  const { isFacebookConnected } = props;
  let accountsWithNames: AdAccountModel[];
  if (isFacebookConnected) {
    accountsWithNames = uniqBy(
      addNamesToAccounts([
        ...(Array.isArray(props?.fbPageAccounts) ? props.fbPageAccounts : []),
        ...(Array.isArray(props?.remoteAccounts) ? props.remoteAccounts : []),
      ]),
      'id',
    );
    accountsWithNames.unshift(emptyAccountPlaceholder);
  } else {
    const selectedAccount = props.fbPageAccounts.find(a => a.selected);
    if (selectedAccount) {
      accountsWithNames = addNamesToAccounts([selectedAccount]);
    } else {
      accountsWithNames = [emptyAccountPlaceholder];
    }
  }

  return {
    ...props,
    accountsWithNames,
  };
};

const handleChange =
  ({
    assignAdAccount,
    getFbPageAccounts,
    fbPageAccounts,
    connectedPage,
    setFbPageAccounts,
    remoteAccounts,
  }: Props) =>
  async (id: string) => {
    if (connectedPage) {
      const adAccount =
        [...fbPageAccounts, ...remoteAccounts].find(acc => acc.id === id) ||
        emptyAccountPlaceholder;
      await assignAdAccount(connectedPage.id, adAccount);
      const newFbPageAccounts = await getFbPageAccounts(connectedPage.id);
      setFbPageAccounts(newFbPageAccounts);
    }
  };

const onScrollEnd = (props: Props) => () => {
  const { getRemoteAccounts, nextCursor, connectedPage } = props;
  const pageId = connectedPage && connectedPage.id;

  if (nextCursor) getRemoteAccounts(pageId, nextCursor);
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withState('fbPageAccounts', 'setFbPageAccounts', [emptyAccountPlaceholder]),
  withState('remoteAccountsRequested', 'setRemoteAccountsRequested', false),
  lifecycle<Props, {}>({ componentDidMount, componentDidUpdate }),
  mapProps(propsMapper),
  withHandlers({ handleChange, onScrollEnd }),
);

export default enhance(AdAccount);
